import URL from 'url-parse';

import { ResolverTag } from '@swf/core/enums';
import type { UseWidget } from '@swf/core/hooks/useWidget';
import { useWidget } from '@swf/core/hooks/useWidget';
import type { Slicer, WidgetEffectParams, WidgetModelConfig } from '@swf/core/store';
import { getState } from '@swf/core/store';
import type { ExtractDispatchFromModel } from '@swf/core/store/types';
import { canUseDOM, canUseLocalStorage } from '@swf/core/utils';
import { fetchJson } from '@swf/core/utils/api';

export interface LegacySportState {
  version: string;
  serviceAvailable: boolean;
}

const reducers = {
  preloaded: (state: LegacySportState, payload: LegacySportState) => payload,
  setServiceAvailable: (state: LegacySportState, serviceAvailable: boolean) => ({
    ...state,
    serviceAvailable
  })
};

const effects = {
  async preload(
    payload: undefined,
    { dispatch, widget: { id }, select, rootState }: WidgetEffectParams<void>
  ) {
    const widgetDispatch: ExtractDispatchFromModel<LegacySportWidgetModel> = dispatch[id];
    await Promise.all(
      [
        ResolverTag.ATSBaseUrl,
        ResolverTag.ATSBootstrap,
        ResolverTag.ATSCss,
        ResolverTag.CasinoAssetWrapper
      ]
        .filter((tag) => !select.resolver.getTag(rootState, tag))
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .map((tag) => dispatch.resolver.fetchTag({ tag }) as Promise<any>)
    );

    const ats = canUseLocalStorage ? JSON.parse(localStorage.getItem('ats') || '{}') : {};
    if (ats.host) return;

    if (canUseDOM) {
      const { url: baseUrl } = select.resolver.getTag(getState(), ResolverTag.ATSBaseUrl);
      try {
        const { version } = await fetchJson(new URL(baseUrl).set('pathname', 'version').toString());
        widgetDispatch.preloaded({ version, serviceAvailable: true });
      } catch (error) {
        widgetDispatch.setServiceAvailable(false);
      }
    }
  }
};

const selectors = (slice: Slicer<LegacySportState>) => ({
  get() {
    return slice;
  }
});

export interface LegacySportWidgetModel extends WidgetModelConfig<void, LegacySportState> {
  reducers: typeof reducers;
  effects: typeof effects;
  selectors: typeof selectors;
}

export const legacySportWidgetModel: LegacySportWidgetModel = {
  state: { version: '', serviceAvailable: true },
  reducers,
  effects,
  selectors
};

export const useLegacySportWidget: UseWidget<void, LegacySportWidgetModel> = useWidget;
